import React from 'react';
import * as Api from 'api/api';
import TextField from '@material-ui/core/TextField';
import Autocomplete from '@material-ui/lab/Autocomplete';
import CircularProgress from '@material-ui/core/CircularProgress';
import Grid from '@material-ui/core/Grid';
import useDebounce from 'utilities/hooks/useDebounce';
import {
  get_empty_normalized_object,
  normalize_objects,
} from 'utilities/general';
import { ModelObra, NormalizedModelObra } from 'api/types';

interface Props {
  disabled?: boolean;
  value: number | null;
  on_change: (event: any, value: number | null) => void;
  client_id?: number | null;
  clientRequired?: boolean;
  required?: boolean;
}

export default function AsyncObraAutoComplete(props: Props) {
  const { clientRequired = true } = props;

  const [search, set_search] = React.useState('');
  const debounced_search = useDebounce(search, 600);
  const [loading, set_loading] = React.useState(false);

  const [selected_obra, set_selected_obra] = React.useState<ModelObra | null>(
    null,
  );

  const [obras, set_obras] = React.useState<NormalizedModelObra>(
    get_empty_normalized_object(),
  );

  // Resolve selected obra by ID
  React.useEffect(() => {
    let active = true;
    const clean = () => {
      active = false;
    };

    if (
      (props.value && !selected_obra) ||
      (props.value &&
        selected_obra &&
        selected_obra.id &&
        props.value !== selected_obra.id)
    ) {
      set_loading(true);
      Api.Obra.detalle(props.value)
        .then((response) => {
          if (active) {
            set_obras(normalize_objects([response.data]));
            set_selected_obra(response.data);
          }
        })
        .catch((error) => {
          console.log(error);
        })
        .finally(() => {
          set_loading(false);
        });
    }

    return clean;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.value]);

  // Search obras with debounced input
  React.useEffect(() => {
    let active = true;
    const clean = () => {
      active = false;
    };

    if (clientRequired && !props.client_id) {
      set_obras(get_empty_normalized_object());
      return clean;
    }

    if (props.value && !selected_obra) return clean;
    if (selected_obra && selected_obra.nombre === debounced_search)
      return clean;

    set_loading(true);
    const filters: Record<string, any> = {
      search: debounced_search,
      limit: 15,
      offset: 0,
    };
    if (props.client_id) {
      filters.cliente_id = props.client_id;
    }

    Api.Obra.listar(filters)
      .then((response) => {
        let _obras = response.data.results;
        if (active) set_obras(_obras);
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        if (active) set_loading(false);
      });

    return clean;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounced_search, props.client_id]);

  const handle_change = (event: any, obra: ModelObra | null) => {
    set_selected_obra(obra ? obra : null);
    props.on_change(event, obra ? obra.id : null);
  };

  const codigoObra = () => {
    if (!selected_obra) return '';
    return selected_obra.codigo;
  };

  return (
    <Grid item xs={12} container spacing={1}>
      <Grid item xs={8}>
        <Autocomplete
          value={selected_obra}
          onChange={handle_change}
          inputValue={search}
          onInputChange={(event, value) => {
            set_search(value);
          }}
          disabled={props.disabled || (clientRequired && !props.client_id)}
          options={obras.ids ? obras.ids.map((id) => obras.por_id[id]) : []}
          getOptionLabel={(option) =>
            option && option.nombre ? option.nombre : ''
          }
          filterOptions={(options, state) => options}
          getOptionSelected={(option, value) => option.id === value.id}
          loading={loading}
          loadingText="Cargando..."
          noOptionsText="Sin opciones"
          renderInput={(params) => (
            <TextField
              {...params}
              label="Obra"
              placeholder="Buscar obra"
              required={props.required}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <React.Fragment>
                    {loading ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </React.Fragment>
                ),
              }}
            />
          )}
        />
      </Grid>
      <Grid item xs={4}>
        <TextField
          label="Código obra"
          name="codigo_obra"
          value={codigoObra()}
          disabled
          fullWidth
        />
      </Grid>
    </Grid>
  );
}
